import './rfqs.scss'

import { Button, DatePicker, notification, Table } from 'antd'
import classnames from 'classnames'
import { Moment } from 'moment'
import { useEffect, useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'
import { BookType } from 'xlsx'

import { fetchDetail } from '../../../api'
import { buildTableFilterInArrayKeys, buildTableFilterKeys } from '../../../components/tables/build-table-filter-keys'
import { config } from '../../../config'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { capitalize } from '../../../formatters'
import { fileTypes, generateFileName, saveFileAs } from '../../../services'
import { generateXlsxFromJson } from '../../../services/generate-xlsx-data'
import { httpClient } from '../../../util/http-client'
import { CancelTradeViaRfqDrawer } from '../CancelTradeViaRfq'
import { messages } from '../messages'
import { convertRfqsToExcel, convertRfqsToTableItems, Rfq, RfqQuote } from '../models'
import { RfqQuotesDrawer } from '../RfqQuotes'
import { buildRfqTableColumns } from './rfq-table-columns'
import { RfqsSummary } from './RfqsSummary'

const { formatIso, now } = momentDateHandler
const { RangePicker }: { RangePicker } = DatePicker

const today = now()

export const Rfqs = () => {
  const [rfqTableItems, setRfqTableItems] = useState([])
  const [summaryRfqs, setSummaryRfqs] = useState([])

  const [filteredRfqs, setFilteredRfqs] = useState(null)
  const [filterMenuItems, setFilterMenuItems] = useState({
    winnerIssuerCodes: [],
    tenors: [],
    statuses: [],
    issuerCodes: [],
    instrumentCodes: [],
    segmentCodes: [],
  })
  const [filterState, setFilterState] = useState(null)

  const [rfqPeriod, setRfqPeriod] = useState<[Moment, Moment]>([today, today])
  const [rfqToRollback, setRfqToRollback] = useState<Rfq>(null)
  const [rfqQuotes, setRfqQuotes] = useState<RfqQuote[]>(null)
  const [currentActiveRow, setCurrentActiveRow] = useState<string>(null)

  const [fromDate, toDate] = rfqPeriod
  const { data, mutate } = useSWR(
    `/reports/rfqs?from=${formatIso({ date: fromDate })}&to=${formatIso({ date: toDate })}`,
    fetchDetail,
    { suspense: true },
  )

  useEffect(() => {
    if (!data) return

    const convertedTableItems = convertRfqsToTableItems(data?.rfqs)
    setRfqTableItems(convertedTableItems)
    setSummaryRfqs(filteredRfqs || convertedTableItems)
    setFilterMenuItems({
      winnerIssuerCodes: buildTableFilterKeys(convertedTableItems, 'winningIssuerCode', capitalize),
      statuses: buildTableFilterKeys(convertedTableItems, 'status', capitalize),
      tenors: buildTableFilterKeys(convertedTableItems, 'tenor', capitalize),
      issuerCodes: buildTableFilterInArrayKeys(convertedTableItems, 'issuerCodes', capitalize),
      instrumentCodes: buildTableFilterInArrayKeys(convertedTableItems, 'instrumentCode', capitalize),
      segmentCodes: buildTableFilterInArrayKeys(convertedTableItems, 'segmentCode', capitalize),
    })
  }, [data, filteredRfqs])

  const onDateChange = (dates: [Moment, Moment]) => {
    if (!dates) return
    const [from, to] = dates
    setRfqPeriod([from, to])
  }

  const onViewQuotes = (quotes: RfqQuote[], rfqUuid: string) => {
    setCurrentActiveRow(rfqUuid)
    setRfqQuotes(quotes)
  }

  const onRollbackRfq = (rfq: Rfq) => {
    setCurrentActiveRow(rfq.uuid)
    setRfqToRollback(rfq)
  }

  const onSubmitSuccess = () => {
    closeDialogs()
    mutate()
  }

  const closeDialogs = () => {
    setCurrentActiveRow(null)
    setRfqQuotes(null)
    setRfqToRollback(null)
  }

  const rfqColumns = buildRfqTableColumns({ onViewQuotes, onRollbackRfq, filterMenuItems, filterState })

  const onTableChange = (_pagination: any, filters: any, _sorter: any, { currentDataSource }: any) => {
    setFilterState(filters)
    setSummaryRfqs(currentDataSource)

    const hasActiveFilters = Object.keys(filters).find((filter) => !!filters[filter])
    setFilteredRfqs(hasActiveFilters ? currentDataSource : null)
  }

  const onResetFilters = () => {
    setFilterState(null)
    setFilteredRfqs(null)
  }

  const downloadRawData = async () => {
    const fileType = fileTypes.csv.extension
    const [from, to] = rfqPeriod
    const fromDate = formatIso({ date: from })
    const toDate = formatIso({ date: to })
    const url = `/reports/rfqs/?from=${fromDate}&to=${toDate}`

    try {
      const { data } = await httpClient(url, { headers: { Accept: 'text/csv' } })
      const name = generateFileName({ prefix: 'raw-data', from: from.toDate(), to: to.toDate() })

      saveFileAs({ content: data, name, fileType })
    } catch (error) {
      notification.error({ message: 'Error', description: messages.rawData.error })
    }
  }

  const downloadRfqsFile = (rfqs: Rfq[], type: 'xlsx' | 'csv') => {
    const fileType = fileTypes[type].extension
    const xlsxData = convertRfqsToExcel(rfqs)
    const content = generateXlsxFromJson({ ...xlsxData, sheetName: 'RFQs', bookType: fileType as BookType })

    const [from, to] = rfqPeriod
    const name = generateFileName({ prefix: 'rfqs', from: from.toDate(), to: to.toDate() })

    saveFileAs({ content, name, fileType })
  }

  return (
    <Column className="rfqs">
      <Row>
        <RangePicker
          size="large"
          format={config.defaultDateFormat}
          defaultValue={[today, today]}
          onChange={onDateChange}
        />
      </Row>

      <RfqsSummary rfqs={summaryRfqs} />

      <Row justifyContentEnd alignItemsCenter className="download-actions">
        <Button onClick={onResetFilters} disabled={!rfqTableItems.length}>
          Reset filters
        </Button>
        Download:
        <Button type="primary" onClick={downloadRawData} disabled={!rfqTableItems.length}>
          Raw Data
        </Button>
        <Button type="primary" onClick={() => downloadRfqsFile(summaryRfqs, 'csv')} disabled={!rfqTableItems.length}>
          CSV
        </Button>
        <Button type="primary" onClick={() => downloadRfqsFile(summaryRfqs, 'xlsx')} disabled={!rfqTableItems.length}>
          Excel
        </Button>
      </Row>

      <Table
        rowClassName={(rfq: Rfq) => classnames({ active: rfq.uuid === currentActiveRow })}
        loading={data === null}
        dataSource={rfqTableItems}
        columns={rfqColumns}
        pagination={false}
        onChange={onTableChange}
        rowKey="uuid"
        size="small"
      />

      <RfqQuotesDrawer rfqQuotes={rfqQuotes} isVisible={!!rfqQuotes} onCancel={closeDialogs} onClose={closeDialogs} />

      <CancelTradeViaRfqDrawer
        isVisible={!!rfqToRollback}
        onClose={closeDialogs}
        rfqToRollback={rfqToRollback}
        onSuccess={onSubmitSuccess}
      />
    </Column>
  )
}
